import { useCallback, useContext } from 'react';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import DefaultBundleContext, {
  MessageArgs,
} from 'src/i18n/shim/DefaultBundleContext';
import { buildLocalizationContext } from 'src/i18n';
import { isSupportedLocale } from 'src/i18n/locales';
import { setStoredLocale } from 'src/i18n/localePreference';

/**
 * Resolves a resource id, applying ICU arguments when given.
 *
 * Exported so modules that build copy outside a component — label maps, diff
 * summaries, option lists — can take `t` as a parameter. Module scope has no
 * hook, so those modules hold resource IDs and are handed this at call time.
 */
export type TranslateFn = (id: string, params?: MessageArgs) => string;

export interface UseTranslationResult {
  t: TranslateFn;
  locale: string;
  setLocale: (locale: string) => void;
}

/**
 * Access localized strings and the active locale. Must be called below `ArbProvider`.
 *
 * A missing provider throws rather than degrading, because the alternative is rendering raw
 * resource ids to users — a failure only visible in production.
 */
export function useTranslation(): UseTranslationResult {
  const bundle = useContext(DefaultBundleContext);
  const { localizationContext, setLocalizationContext } =
    useLocalizationContext();

  if (bundle === undefined) {
    throw new Error(
      'useTranslation requires an <ArbProvider> ancestor. A provider rendered as a sibling ' +
        'rather than an ancestor will not satisfy this.',
    );
  }

  const t = useCallback<TranslateFn>(
    (id, params) =>
      params ? bundle.formatMessage(id, params) : bundle.getMessage(id),
    [bundle],
  );

  const setLocale = useCallback(
    (next: string) => {
      // Rejected here as well as in setStoredLocale: without this an unsupported tag would be
      // applied to the live context and rendered while storage silently dropped it, so the
      // rendered locale, the `lang` attribute and the persisted value would disagree.
      if (!isSupportedLocale(next)) {
        return;
      }
      setStoredLocale(next);
      setLocalizationContext(buildLocalizationContext(next));
    },
    [setLocalizationContext],
  );

  return {
    t,
    locale: localizationContext.getLocale(),
    setLocale,
  };
}
